import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, TrendingUp, AlertTriangle, CalendarClock, Users, Ticket,
  ArrowUpRight, Info, ChevronRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { reportsAPI } from '../services/PaymentReportsApi';

/**
 * What the money is doing, and what it could be doing.
 *
 * Sits beside FinanceReports rather than inside it. That answers "what came in
 * last month" from the transaction ledger; this asks the forward questions —
 * how full are we, what is being left on the table, what is next month likely
 * to bring — which need the sessions and their prices, not the ledger.
 *
 * ── Two rules this screen holds to ──────────────────────────────────────────
 *
 * 1. NEVER PRESENT AN ESTIMATE AS A FACT. Realised revenue and projected
 *    revenue never share a visual treatment. Every projected figure carries its
 *    basis in the same glance — how many past sessions it was inferred from —
 *    and a range rather than a point.
 *
 * 2. EVERY FINDING NAMES ITS LEVER. A number with no action attached is a mood.
 *    The leakage cards each say what the number is, why it exists, and what
 *    moves it.
 *
 * Colour follows FinanceReports: fixed neutral plane, palette validated against
 * it, and no figure carried by colour alone.
 */

const money = (n, currency = 'USD') => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: v % 1 ? 2 : 0,
    }).format(v);
  } catch { return `${currency} ${v.toFixed(2)}`; }
};

const DAYS = [
  { code: 'MON', label: 'Mon' }, { code: 'TUE', label: 'Tue' }, { code: 'WED', label: 'Wed' },
  { code: 'THU', label: 'Thu' }, { code: 'FRI', label: 'Fri' }, { code: 'SAT', label: 'Sat' },
  { code: 'SUN', label: 'Sun' },
];

const SEVERITY = {
  high:   { dot: 'bg-red-500',    label: 'text-red-600 dark:text-red-400' },
  medium: { dot: 'bg-amber-500',  label: 'text-amber-600 dark:text-amber-400' },
  low:    { dot: 'bg-gray-400',   label: 'text-gray-500' },
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ─── Small pieces ────────────────────────────────────────────────────────────

const Kpi = ({ label, value, sub, tone = 'plain', theme, icon: Icon }) => (
  <div className={`${theme.card} border rounded-xl p-4`}>
    <div className={`flex items-center gap-1.5 text-xs ${theme.textMuted}`}>
      {Icon && <Icon size={13} />}
      {label}
    </div>
    <p className={`text-2xl font-semibold mt-1 ${
      tone === 'estimate' ? `${theme.text} italic` : theme.text
    }`}>
      {value}
    </p>
    {sub && <p className={`text-xs ${theme.textMuted} mt-0.5`}>{sub}</p>}
  </div>
);

/**
 * Day × hour, coloured by fill rate — but every cell also prints its number,
 * so the colour is a scan aid rather than the data.
 */
const DemandGrid = ({ cells, threshold, theme }) => {
  const hours = useMemo(
    () => [...new Set(cells.map(c => c.hour))].sort((a, b) => a - b),
    [cells],
  );
  if (!cells.length) return <p className={`text-sm ${theme.textMuted}`}>No sessions in this range.</p>;

  const byKey = new Map(cells.map(c => [`${c.day}-${c.hour}`, c]));
  const shade = (fill) => {
    if (fill == null) return '';
    if (fill >= 80) return 'bg-emerald-600 text-white';
    if (fill >= 50) return 'bg-emerald-400/70 text-black';
    if (fill >= 25) return 'bg-amber-300/70 text-black';
    if (fill > 0)   return 'bg-orange-300/60 text-black';
    return 'bg-red-200/60 text-black dark:bg-red-900/40 dark:text-red-200';
  };

  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-separate" style={{ borderSpacing: '2px' }}>
        <thead>
          <tr>
            <th className={`text-left font-medium ${theme.textMuted} pr-2`}>Hour</th>
            {DAYS.map(d => <th key={d.code} className={`font-medium ${theme.textMuted} px-1`}>{d.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {hours.map(h => (
            <tr key={h}>
              <td className={`pr-2 ${theme.textMuted} whitespace-nowrap`}>
                {String(h).padStart(2, '0')}:00
              </td>
              {DAYS.map(d => {
                const c = byKey.get(`${d.code}-${h}`);
                if (!c) return <td key={d.code} className={`rounded ${theme.backgroundSecondary} opacity-40`} />;
                const thin = c.sessions < threshold;
                return (
                  <td
                    key={d.code}
                    title={`${c.sessions} session${c.sessions === 1 ? '' : 's'} · ${c.bookings}/${c.seats} seats · ${money(c.revenuePerSession)} per session`}
                    className={`rounded px-2 py-1 text-center ${shade(c.fillRate)} ${thin ? 'opacity-50' : ''}`}
                  >
                    <span className="font-semibold">{c.fillRate == null ? '—' : `${c.fillRate}%`}</span>
                    <span className="block text-[10px] opacity-80">{c.sessions}×</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={`text-xs ${theme.textMuted} mt-2 flex items-center gap-1.5`}>
        <Info size={12} />
        Fill rate, with session count beneath. Faded cells have fewer than {threshold} sessions —
        too few to read as a pattern.
      </p>
    </div>
  );
};

// ─── The screen ──────────────────────────────────────────────────────────────

const RevenueIntelligence = ({ academyId }) => {
  const { theme } = useTheme();

  const [filters, setFilters] = useState({
    from: '', to: '', programType: '', level: '', days: [], weeks: 8,
  });
  const [data, setData]       = useState({ overview: null, leakage: null, demand: null, projection: null });
  const [loading, setLoading] = useState(true);
  const [openFinding, setOpenFinding] = useState(null);

  const query = useMemo(() => ({
    academyId,
    from: filters.from || undefined,
    to: filters.to || undefined,
    programType: filters.programType || undefined,
    level: filters.level || undefined,
    days: filters.days.length ? filters.days.join(',') : undefined,
  }), [academyId, filters]);

  const load = useCallback(async () => {
    if (!academyId) return;
    setLoading(true);
    try {
      // Four requests rather than one: they answer different questions, and a
      // slow projection should not hold up the headline.
      const [overview, leakage, demand, projection] = await Promise.all([
        reportsAPI.overview(query),
        reportsAPI.leakage(query),
        reportsAPI.demand(query),
        reportsAPI.projection({ ...query, weeks: filters.weeks }),
      ]);
      setData({ overview, leakage, demand, projection });
    } catch (err) {
      toast.error(err?.message || 'Could not load reports');
    } finally {
      setLoading(false);
    }
  }, [academyId, query, filters.weeks]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const { overview, leakage, demand, projection } = data;

  const input = `px-3 py-2 rounded-lg border ${theme.border} ${theme.card} ${theme.text} text-sm`;

  return (
    <div className="space-y-5">
      <div>
        <h2 className={`text-xl font-bold ${theme.text}`}>Revenue intelligence</h2>
        <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
          Programmes and open play together — what came in, what is on the table, and what is coming.
        </p>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className={`${theme.card} border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3`}>
        <div>
          <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>From</label>
          <input type="date" value={filters.from} onChange={e => set('from', e.target.value)} className={`w-full ${input}`} />
        </div>
        <div>
          <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>To</label>
          <input type="date" value={filters.to} onChange={e => set('to', e.target.value)} className={`w-full ${input}`} />
        </div>
        <div>
          <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Offering type</label>
          <select value={filters.programType} onChange={e => set('programType', e.target.value)} className={`w-full ${input}`}>
            <option value="">All</option>
            <option value="open_play">Open play</option>
            <option value="meetup">Meetup</option>
            <option value="course">Course</option>
          </select>
        </div>
        <div>
          <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Level</label>
          <select value={filters.level} onChange={e => set('level', e.target.value)} className={`w-full ${input}`}>
            <option value="">Any</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <label className={`block text-xs font-medium ${theme.textMuted} mb-1`}>Days</label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map(d => {
              const on = filters.days.includes(d.code);
              return (
                <button key={d.code} type="button"
                  onClick={() => set('days', on ? filters.days.filter(c => c !== d.code) : [...filters.days, d.code])}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                    on ? 'bg-blue-600 text-white border-transparent' : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`}`}>
                  {d.label}
                </button>
              );
            })}
            {filters.days.length > 0 && (
              <button type="button" onClick={() => set('days', [])} className={`px-2 py-1 text-xs ${theme.textMuted} hover:underline`}>
                All days
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && !overview ? (
        <div className={`flex items-center justify-center gap-2 py-16 ${theme.textMuted}`}>
          <Loader2 size={18} className="animate-spin" /> <span className="text-sm">Reading the books…</span>
        </div>
      ) : (
        <>
          {/* ── What happened ───────────────────────────────────────────── */}
          {overview && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi theme={theme} icon={TrendingUp} label="Collected"
                   value={money(overview.revenue.realised)}
                   sub={[
                     `${money(overview.revenue.bySource.openPlay.paid)} open play`,
                     `${money(overview.revenue.bySource.programs.paid)} programmes`,
                     `${money(overview.revenue.bySource.tournaments?.paid || 0)} tournaments`,
                   ].join(' · ')} />
              <Kpi theme={theme} icon={Ticket} label="Booked, not yet paid"
                   value={money(overview.revenue.promised)}
                   sub={`${overview.revenue.bySource.openPlay.orders} orders · ${overview.enrollments.programEnrollments} active enrolments`} />
              <Kpi theme={theme} icon={Users} label="Fill rate"
                   value={overview.occupancy.fillRate == null ? '—' : `${overview.occupancy.fillRate}%`}
                   sub={`${overview.occupancy.seatsTaken} of ${overview.occupancy.seatsOffered} seats · ${overview.occupancy.sessions} sessions`} />
              <Kpi theme={theme} icon={ArrowUpRight} label="Value of unsold seats"
                   value={money(overview.potential.unsoldSeatValue)}
                   sub="capacity × price, not a forecast" />
            </div>
          )}

          {overview?.occupancy.sessionsUnlimited > 0 && (
            <p className={`text-xs ${theme.textMuted} flex items-center gap-1.5`}>
              <Info size={12} />
              {overview.occupancy.sessionsUnlimited} session{overview.occupancy.sessionsUnlimited === 1 ? '' : 's'} have
              no capacity set and are excluded from the fill rate — there is no denominator to divide by.
            </p>
          )}

          {/* ── What is coming ──────────────────────────────────────────── */}
          {projection && (
            <div className={`${theme.card} border rounded-xl p-4`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className={`font-semibold ${theme.text} flex items-center gap-2`}>
                    <CalendarClock size={16} /> Next {projection.horizonWeeks} weeks
                  </h3>
                  <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                    {projection.sessions} scheduled session{projection.sessions === 1 ? '' : 's'}
                  </p>
                </div>
                <select value={filters.weeks} onChange={e => set('weeks', Number(e.target.value))} className={input}>
                  {[4, 8, 12, 26].map(w => <option key={w} value={w}>{w} weeks</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div>
                  <p className={`text-xs ${theme.textMuted}`}>Already sold</p>
                  <p className={`text-2xl font-semibold ${theme.text}`}>{money(projection.committed)}</p>
                  <p className={`text-xs ${theme.textMuted}`}>seats taken on future sessions</p>
                </div>
                <div>
                  <p className={`text-xs ${theme.textMuted}`}>Expected</p>
                  {/* Italic and banded, so it never reads like the figure to its
                      left. An estimate that looks like a fact is the failure
                      mode of every revenue dashboard. */}
                  <p className={`text-2xl font-semibold italic ${theme.text}`}>{money(projection.expected)}</p>
                  <p className={`text-xs ${theme.textMuted}`}>
                    {money(projection.range.low)} – {money(projection.range.high)}
                  </p>
                </div>
                <div>
                  <p className={`text-xs ${theme.textMuted}`}>Based on</p>
                  <p className={`text-sm ${theme.text} mt-1`}>
                    {projection.basis.historicalFillRate == null
                      ? 'No history yet — expected is what is already sold.'
                      : `${projection.basis.historicalFillRate}% historical fill across ${projection.basis.slotsWithHistory} time slots`}
                  </p>
                  {projection.basis.sessionsWithNoBasis > 0 && (
                    <p className={`text-xs ${theme.textMuted} mt-1`}>
                      {projection.basis.sessionsWithNoBasis} session{projection.basis.sessionsWithNoBasis === 1 ? '' : 's'} with
                      no comparable history — counted at what is already sold rather than guessed.
                    </p>
                  )}
                  {projection.basis.unpricedSessions > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      {projection.basis.unpricedSessions} upcoming session{projection.basis.unpricedSessions === 1 ? '' : 's'} carry no price.
                    </p>
                  )}
                </div>
              </div>
              <p className={`text-xs ${theme.textMuted} mt-3`}>{projection.range.note}</p>
            </div>
          )}

          {/* ── Where money is left behind ──────────────────────────────── */}
          {leakage && (
            <div className={`${theme.card} border rounded-xl p-4`}>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h3 className={`font-semibold ${theme.text} flex items-center gap-2`}>
                  <AlertTriangle size={16} /> Money left behind
                </h3>
                <div className={`text-xs ${theme.textMuted}`}>
                  <span className={theme.text}>{money(leakage.owed)}</span> owed ·{' '}
                  <span className={theme.text}>{money(leakage.forgone)}</span> forgone
                </div>
              </div>
              {/* Kept apart on purpose: owed is collectable, forgone is not.
                  One headline adding them would overstate what can be chased. */}

              {leakage.findings.length === 0 ? (
                <p className={`text-sm ${theme.textMuted} mt-3`}>Nothing outstanding in this range.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {leakage.findings.map(f => {
                    const sev = SEVERITY[f.severity] || SEVERITY.low;
                    const open = openFinding === f.key;
                    return (
                      <div key={f.key} className={`border ${theme.border} rounded-lg`}>
                        <button
                          onClick={() => setOpenFinding(open ? null : f.key)}
                          className={`w-full flex items-center gap-3 p-3 text-left ${theme.cardHover}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${sev.dot} flex-shrink-0`} />
                          <span className="min-w-0 flex-1">
                            <span className={`block text-sm font-medium ${theme.text}`}>{f.title}</span>
                            <span className={`block text-xs ${theme.textMuted}`}>{f.count} · {f.why}</span>
                          </span>
                          <span className={`text-sm font-semibold ${theme.text} flex-shrink-0`}>
                            {f.amount == null ? '—' : money(f.amount)}
                          </span>
                          <ChevronRight size={14} className={`${theme.textMuted} transition-transform ${open ? 'rotate-90' : ''}`} />
                        </button>
                        {open && (
                          <div className={`px-3 pb-3 border-t ${theme.border} pt-2`}>
                            <p className={`text-xs ${sev.label} font-medium mb-2`}>{f.action}</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <tbody>
                                  {f.rows.map((r, i) => (
                                    <tr key={i} className={`border-t ${theme.border}`}>
                                      <td className={`py-1.5 pr-3 ${theme.text}`}>
                                        {r.session || r.program || r.player || '—'}
                                      </td>
                                      <td className={`py-1.5 pr-3 ${theme.textMuted} whitespace-nowrap`}>
                                        {r.startsAt ? new Date(r.startsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                                      </td>
                                      <td className={`py-1.5 pr-3 ${theme.textMuted}`}>
                                        {r.capacity != null ? `${r.booked}/${r.capacity}` : ''}
                                        {r.queued ? `+${r.queued} waiting` : ''}
                                      </td>
                                      <td className={`py-1.5 text-right ${theme.text} whitespace-nowrap`}>
                                        {money(r.forgone ?? r.unmet ?? r.amount ?? 0)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── When people actually play ───────────────────────────────── */}
          {demand && (
            <div className={`${theme.card} border rounded-xl p-4`}>
              <h3 className={`font-semibold ${theme.text} flex items-center gap-2 mb-3`}>
                <Users size={16} /> When people actually play
              </h3>
              <DemandGrid cells={demand.cells} threshold={demand.confidenceThreshold} theme={theme} />

              {(demand.strongest.length > 0 || demand.weakest.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className={`text-xs font-medium ${theme.textSecondary} mb-1`}>Best slots</p>
                    {demand.strongest.map(c => (
                      <p key={`${c.day}-${c.hour}`} className={`text-xs ${theme.text}`}>
                        {c.day} {String(c.hour).padStart(2, '0')}:00 — {c.fillRate}% full,
                        {' '}{money(c.revenuePerSession)} per session
                      </p>
                    ))}
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${theme.textSecondary} mb-1`}>Quietest slots</p>
                    {demand.weakest.map(c => (
                      <p key={`${c.day}-${c.hour}`} className={`text-xs ${theme.text}`}>
                        {c.day} {String(c.hour).padStart(2, '0')}:00 — {c.fillRate}% full,
                        {' '}{money(c.revenuePerSession)} per session
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RevenueIntelligence;
